package top.wcpe.allincore.ui.runtime.style

import kotlin.jvm.JvmStatic

/**
 * 元素视觉 modifier 链（ADR-0041）。
 *
 * [basePaint] 是盒级底层绘制（阴影、表面），[effectPaint] 是盒级叠加效果（程序纹理、霓虹、流光、能量），
 * [backdropPaint] 是元素自身绘制前的一次性背景采样效果，[scoped] 是包裹元素与子树的作用域效果，
 * [particleLayers] 是 FR-UI-031 粒子层独立段（由 UIRenderer 按 front/back 注入延迟绘制命令，不走 paint fold）。
 *
 * 分段保留现有渲染插入点：例如 BoxRenderer 需要在 basePaint 与 effectPaint 之间插入主题 scanline。
 */
data class VisualModifierChain(
    val basePaint: List<VisualModifier> = emptyList(),
    val effectPaint: List<VisualModifier> = emptyList(),
    val backdropPaint: List<VisualModifier> = emptyList(),
    val scoped: List<ScopedVisualModifier> = emptyList(),
    val particleLayers: List<VisualModifier.ParticleLayer> = emptyList(),
) {
    /** 盒级 paint 链：不含 backdrop，供普通 box paint fold 使用。 */
    val boxPaint: List<VisualModifier>
        get() = basePaint + effectPaint

    /** 是否没有任何视觉 modifier。 */
    fun isEmpty(): Boolean = basePaint.isEmpty() &&
        effectPaint.isEmpty() &&
        backdropPaint.isEmpty() &&
        scoped.isEmpty() &&
        particleLayers.isEmpty()

    companion object {
        val EMPTY: VisualModifierChain = VisualModifierChain()

        /** 从旧 [StyleVisual] 兼容模型脱糖为一等 modifier 链。 */
        @JvmStatic
        fun fromVisual(visual: StyleVisual, backgroundColor: Int): VisualModifierChain = VisualModifierChain(
            basePaint = StyleVisualModifiers.toBoxBaseModifiers(visual, backgroundColor),
            effectPaint = StyleVisualModifiers.toBoxEffectModifiers(visual),
            backdropPaint = StyleVisualModifiers.toBackdropModifiers(visual),
            scoped = StyleVisualModifiers.toScopedModifiers(visual),
        )
    }
}
